import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { usePublicPrices } from "@/lib/usePublicPrices";
import AppShell from "@/components/AppShell";
import SmartMatches from "@/components/SmartMatches";
import PersonaPickerDialog from "@/components/PersonaPickerDialog";
import AdminAlertStrip from "@/components/AdminAlertStrip";
import SellerWelcomeBanner from "@/components/SellerWelcomeBanner";
import VerifiedSealKitAnnouncement from "@/components/VerifiedSealKitAnnouncement";
import CreateKennelNudge from "@/components/CreateKennelNudge";
import WidgetBoundary from "@/components/WidgetBoundary";
import ProviderApplicationNudge from "@/components/ProviderApplicationNudge";
import NextStepNudge from "@/components/NextStepNudge";
import TodayWithDogsWidget from "@/components/TodayWithDogsWidget";
import DogLogStreaksWidget from "@/components/DogLogStreaksWidget";
import PersonaGreeting from "@/components/PersonaGreeting";
import MultiHatCards from "@/components/MultiHatCards";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import PersonaToolkitCard from "@/components/PersonaToolkitCard";
import TransporterApplicationNudge from "@/components/TransporterApplicationNudge";
import TransporterDashboardBody from "@/components/TransporterDashboardBody";
import InternationalTransporterDashboardBody from "@/components/InternationalTransporterDashboardBody";
import { ProviderDashboardBody, StorefrontDashboardBody } from "@/components/PersonaDashboardBodies";
import CalendarWidget from "@/components/CalendarWidget";
import BreederBreedingHubCard from "@/components/BreederBreedingHubCard";
import StripeConnectBanner from "@/components/StripeConnectBanner";
import PaymentRecoveryBanner from "@/components/PaymentRecoveryBanner";
import UnreadThreadsNudge from "@/components/UnreadThreadsNudge";
import ContractsAwaitingBanner from "@/components/ContractsAwaitingBanner";
import HomeTrackerWidget from "@/components/HomeTrackerWidget";
import { DogIcon as Dog, PawPrintIcon as PawPrint, UsersFourIcon as UsersFour, GenderMaleIcon as GenderMale, GenderFemaleIcon as GenderFemale, PlusIcon as Plus, MagnifyingGlassIcon as MagnifyingGlass, StorefrontIcon as Storefront, ArrowRightIcon as ArrowRight, HeartIcon as Heart, HeartStraightIcon as HeartStraight, TruckIcon as Truck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { formatDate, thumbUrl } from "@/lib/utils-app";
import { useAuth } from "@/contexts/AuthContext";
import BreederPlanPromo from "@/components/BreederPlanPromo";
import SellerPayoutWaitlist from "@/components/SellerPayoutWaitlist";
import { LuxuryCard, NoiseOverlay, Icon, Indicium } from "@/components/luxury";
import { isNativeApp } from "@/lib/nativeBridge";

const PERSONA_HEADLINE = {
  breeder: "Your kennel, at a glance",
  owner: "Your dogs, at a glance",
  buyer: "Find your next dog",
  transporter: "Your transport hub, at a glance",
  service_provider: "Your service business, at a glance",
  storefront_seller: "Your Storefront, at a glance",
};

const PERSONA_NEW_DOG_LABEL = {
  breeder: "New dog",
  owner: "Add my dog",
  buyer: "Save a dog",
  // Transporters don't typically register dogs themselves — the
  // primary CTA reads "View routes" so the dashboard remains useful
  // if they ever browse the marketplace. (Dashboard sets the destination
  // separately via persona-aware routing logic.)
  transporter: "View routes",
  // Service providers + storefront sellers see CTAs that route them
  // to their primary surface, not "Add a dog" which would be
  // confusing for someone who joined to sell training, not register
  // a dog.
  service_provider: "Bookings",
  storefront_seller: "Manage store",
};

// Gold-gradient stat tile — Spectral light display numeral with tabular
// figures, drop-shadow on hover, and a subtle gold hairline that reveals
// on hover. The icon sits in an Indicium (cream paper + gold hairline
// ring + letterpress shadow) so it reads as a heritage stamp, not a
// floating duotone glyph.
const Stat = ({ icon: IconCmp, label, value, tone = "ink", testId, to }) => {
  const inner = (
    <LuxuryCard interactive className="p-3 sm:p-6 group min-w-0 h-full" data-testid={testId}>
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <Indicium
          icon={IconCmp}
          tone={tone}
          size="md"
          className="transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="text-2xl sm:text-4xl md:text-5xl font-spectral font-light tracking-tight text-ink font-tabular leading-none">
        {value}
      </div>
      <div className="text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#6B6B6B] mt-2 sm:mt-3 line-clamp-2 leading-tight">{label}</div>
      <div className="gold-hairline mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </LuxuryCard>
  );
  // Stat cards on the dashboard are entry points to the matching
  // surface — clicking "Dogs" should land you on My Dogs, etc. We
  // wrap the card in a Link only when `to` is provided so any caller
  // that doesn't pass one keeps the legacy non-interactive behavior.
  if (!to) return inner;
  return (
    <Link to={to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-2xl">
      {inner}
    </Link>
  );
};

/** Recent-dogs + recent-litters columns — shown to breeders, owners, and
 *  the default-persona viewer. Unchanged from the original dashboard. */
function StandardFeed({ stats }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <LuxuryCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-3">
            <Indicium icon={Dog} tone="ink" size="sm" />
            Recent dogs
          </h2>
          <Link to="/dogs" className="text-sm text-ink hover:text-crimson transition-colors" data-testid="view-all-dogs">
            View all
          </Link>
        </div>
        <div className="gold-hairline mb-2" />
        <div className="divide-y divide-[#E5E5E5]">
          {(stats?.recent_dogs || []).map((d) => (
            <Link
              key={d.id}
              to={`/dogs/${d.id}`}
              className="flex items-center gap-4 py-3 hover:bg-cream/40 -mx-2 px-2 rounded-lg transition-colors"
              data-testid={`recent-dog-${d.id}`}
            >
              <div className="h-12 w-12 rounded-lg bg-cream overflow-hidden flex-shrink-0 ring-1 ring-[#E2DAC2]">
                {d.photos?.[0] ? (
                  <img src={thumbUrl(d.photos[0])} alt={d.name} className="h-full w-full object-cover photo-luxury" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <Dog size={22} className="text-[#6B6B6B]" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.name}</div>
                <div className="text-xs text-[#6B6B6B] truncate">
                  {d.breed} · {d.sex}
                </div>
              </div>
              <div className="text-xs text-[#6B6B6B] font-tabular">{formatDate(d.created_at)}</div>
            </Link>
          ))}
          {stats && stats.recent_dogs?.length === 0 && (
            <div className="text-sm text-[#6B6B6B] py-6 text-center">
              No dogs registered yet. <Link to="/dogs/new" className="text-ink underline underline-offset-4 hover:text-crimson">Add one</Link>.
            </div>
          )}
        </div>
      </LuxuryCard>

      <LuxuryCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-3">
            <Indicium icon={PawPrint} tone="ink" size="sm" />
            Recent litters
          </h2>
          <Link to="/litters" className="text-sm text-ink hover:text-crimson transition-colors" data-testid="view-all-litters">
            View all
          </Link>
        </div>
        <div className="gold-hairline mb-2" />
        <div className="divide-y divide-[#E5E5E5]">
          {(stats?.recent_litters || []).map((l) => (
            <Link
              key={l.id}
              to={`/litters/${l.id}`}
              className="block py-3 hover:bg-cream/40 -mx-2 px-2 rounded-lg transition-colors"
              data-testid={`recent-litter-${l.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">
                  {l.sire_name} × {l.dam_name}
                </div>
                <span className="text-xs text-crimson bg-crimson/10 rounded-full px-3 py-1 font-tabular">
                  {l.puppy_ids?.length || 0} pups
                </span>
              </div>
              <div className="text-xs text-[#6B6B6B] mt-1 font-tabular">Whelped {formatDate(l.whelp_date)}</div>
            </Link>
          ))}
          {stats && stats.recent_litters?.length === 0 && (
            <div className="text-sm text-[#6B6B6B] py-6 text-center">
              No litters recorded yet.
            </div>
          )}
        </div>
      </LuxuryCard>
    </div>
  );
}

/** Watching section shown above the "Kennels to explore" / "Recent litters"
 *  columns when the viewer holds a Buyer Pass and has bookmarked items. */
function WatchingSection({ stats }) {
  const breeders = stats?.watching_breeders || [];
  const litters = stats?.watching_litters || [];
  if (breeders.length === 0 && litters.length === 0) return null;

  return (
    <LuxuryCard tone="cream" className="mb-8 p-6" data-testid="watching-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Indicium icon={Heart} tone="gold" size="sm" />
          <h2 className="font-spectral text-2xl tracking-tight">Watching</h2>
          {!isNativeApp() && (
          <span className="text-[10px] bg-gold/15 text-[#8a6f10] border border-gold/40 rounded-full px-2 py-0.5 font-bold uppercase tracking-[0.15em]">
            Buyer Pass
          </span>
          )}
        </div>
      </div>
      <div className="gold-hairline mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
        {breeders.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">
              Kennels
            </p>
            <div className="divide-y divide-[#E8D9A6]/60">
              {breeders.map((b) => {
                const location = [b.city, b.state || b.country].filter(Boolean).join(", ");
                return (
                  <Link
                    key={b.id}
                    to={`/breeders/${b.id}`}
                    className="flex items-center gap-3 py-2 hover:bg-white/50 -mx-2 px-2 rounded-md"
                    data-testid={`watching-breeder-${b.id}`}
                  >
                    <Storefront size={18} weight="duotone" className="text-gold flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{b.kennel_name || b.name}</div>
                      {location && <div className="text-xs text-[#6B6B6B] truncate">{location}</div>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        {litters.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">
              Litters
            </p>
            <div className="divide-y divide-[#E8D9A6]/60">
              {litters.map((l) => (
                <Link
                  key={l.id}
                  to={`/litters`}
                  className="flex items-center gap-3 py-2 hover:bg-white/50 -mx-2 px-2 rounded-md"
                  data-testid={`watching-litter-${l.id}`}
                >
                  <PawPrint size={18} weight="duotone" className="text-crimson flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {l.sire_name} × {l.dam_name}
                    </div>
                    <div className="text-xs text-[#6B6B6B] truncate">
                      {l.pup_count} pups · whelped {formatDate(l.whelp_date)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </LuxuryCard>
  );
}

/** Nudge visible to buyers who haven't purchased a Buyer Pass yet. */
function BuyerPassPromo() {
  // App Store 3.1.1 — no subscription promos/prices in the native shell.
  const { prices } = usePublicPrices();
  if (isNativeApp()) return null;
  return (
    <LuxuryCard tone="dark" className="relative mb-8 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 overflow-hidden" data-testid="buyer-pass-promo">
      <NoiseOverlay opacity={0.06} />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 90% 50%, rgba(201,162,39,0.18), transparent 55%)",
        }}
      />
      <div className="relative flex items-start gap-4">
        <Indicium icon={Heart} tone="gold" size="lg" variant="dark" />
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold-light mb-1.5">
            Buyer Pass
          </p>
          <h3 className="font-spectral text-xl md:text-2xl tracking-tight">Save the kennels &amp; litters you're watching</h3>
          <p className="text-sm text-white/75 mt-1.5 max-w-xl leading-relaxed">
            Bookmark any kennel or litter, get alerts when matching litters are registered, and unlock the full 6-generation pedigree on any dog. <span className="font-tabular">{prices("buyer_pass_monthly", "$4.99")}/mo or {prices("buyer_pass_annual", "$39")}/yr</span>.
          </p>
        </div>
      </div>
      <Link to="/billing" className="relative flex-shrink-0">
        <Button className="bg-gold-gradient hover:opacity-95 text-ink font-semibold rounded-full px-6 h-11 shadow-[0_8px_24px_rgba(201,162,39,0.35)] border-0" data-testid="buyer-pass-upgrade-btn">
          Unlock Buyer Pass
        </Button>
      </Link>
    </LuxuryCard>
  );
}

/** Buyer-focused feed — featured kennels + recent litters with pups.
 *  Replaces the "recent dogs / recent litters" columns when persona=buyer. */
function BuyerFeed({ stats }) {
  const breeders = stats?.featured_breeders || [];
  const litters = stats?.available_litters || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="buyer-feed">
      <LuxuryCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-3">
            <Indicium icon={UsersFour} tone="ink" size="sm" />
            Kennels to explore
          </h2>
          <Link to="/breeders" className="text-sm text-ink hover:text-crimson transition-colors" data-testid="view-all-breeders">
            View all
          </Link>
        </div>
        <div className="gold-hairline mb-2" />
        <div className="divide-y divide-[#E5E5E5]">
          {breeders.map((b) => {
            const location = [b.city, b.state || b.country].filter(Boolean).join(", ");
            return (
              <Link
                key={b.id}
                to={`/breeders/${b.id}`}
                className="flex items-center gap-4 py-3 hover:bg-cream/40 -mx-2 px-2 rounded-lg transition-colors"
                data-testid={`buyer-breeder-${b.id}`}
              >
                <div className="h-12 w-12 rounded-lg bg-cream border border-[#E2DAC2] overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {b.logo ? (
                    <img src={thumbUrl(b.logo)} alt={b.name} className="h-full w-full object-cover photo-luxury" />
                  ) : (
                    <Storefront size={22} weight="duotone" className="text-gold" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{b.kennel_name || b.name}</div>
                  {location && <div className="text-xs text-[#6B6B6B] truncate">{location}</div>}
                </div>
                <ArrowRight size={14} weight="bold" className="text-[#6B6B6B] flex-shrink-0" />
              </Link>
            );
          })}
          {stats && breeders.length === 0 && (
            <div className="text-sm text-[#6B6B6B] py-6 text-center">
              No kennels listed yet — check back soon.
            </div>
          )}
        </div>
      </LuxuryCard>

      <LuxuryCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-3">
            <Indicium icon={PawPrint} tone="ink" size="sm" />
            Recently listed puppies
          </h2>
          <Link to="/litters" className="text-sm text-ink hover:text-crimson transition-colors" data-testid="view-all-litters">
            View all
          </Link>
        </div>
        <div className="gold-hairline mb-2" />
        <div className="divide-y divide-[#E5E5E5]">
          {litters.map((l) => (
            <div key={l.id} className="py-3" data-testid={`buyer-litter-${l.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium truncate pr-2">
                  {l.sire_name} × {l.dam_name}
                </div>
                <span className="text-xs text-crimson bg-crimson/10 rounded-full px-3 py-1 flex-shrink-0 font-tabular">
                  {l.puppy_ids?.length || 0} pups
                </span>
              </div>
              <div className="text-xs text-[#6B6B6B] mt-1 font-tabular">Whelped {formatDate(l.whelp_date)}</div>
            </div>
          ))}
          {stats && litters.length === 0 && (
            <div className="text-sm text-[#6B6B6B] py-6 text-center">
              No recent litters — new ones show up here as they're listed.
            </div>
          )}
        </div>
      </LuxuryCard>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth() || {};
  const [stats, setStats] = useState(null);
  const [persona, setPersona] = useState(null);
  const isAdmin = user?.role === "admin";

  // Re-fetch stats whenever persona changes: the backend branches on the
  // viewer's persona (buyer gets a curated feed, everyone else gets the
  // recent-dogs/litters slices) so a stale stats blob would mismatch.
  const loadStats = useCallback(() => {
    api.get("/dashboard/stats").then((r) => {
      setStats(r.data);
      if (r.data?.persona) setPersona(r.data.persona);
    });
  }, []);

  useEffect(() => {
    if (isAdmin) return; // admins get redirected — skip the data fetch
    loadStats();
    // Resolve persona with a two-step fallback:
    //   1. /me/persona — the user's self-declared role (set via
    //      PersonaPickerDialog or Settings).
    //   2. /me/nav-context — the resolver's data-derived primary
    //      (catches service providers, storefront sellers, etc. who
    //      joined via their dedicated onboarding flow and never
    //      touched the picker).
    // The self-declared value wins when present so a breeder who
    // also has a Storefront keeps their chosen breeder dashboard.
    api.get("/me/persona")
      .then((r) => {
        const declared = r.data?.persona || null;
        if (declared) {
          setPersona(declared);
          return;
        }
        api.get("/me/nav-context")
          .then((nav) => setPersona(nav.data?.primary || null))
          .catch(() => {});
      })
      .catch(() => {});
  }, [loadStats, isAdmin]);

  // Listen for persona changes from the dialog or Settings so the
  // headline, CTAs, and feed all refresh without a navigation.
  const onPersonaChanged = useCallback((e) => {
    const next = e?.detail?.persona || null;
    setPersona(next);
    loadStats();
  }, [loadStats]);
  useEffect(() => {
    if (isAdmin) return;
    window.addEventListener("persona-changed", onPersonaChanged);
    return () => window.removeEventListener("persona-changed", onPersonaChanged);
  }, [onPersonaChanged, isAdmin]);

  // Admins don't have a "kennel dashboard" — none of these cards
  // (find-a-dog, plan-a-breeding, payouts ready, kennel earnings)
  // pertain to admin work. AdminOperations at /admin already shows
  // the 19 action-needed cards admins actually use. Bounce them
  // there. Hooks above run first so React's hook order stays stable.
  // Mobile drawer "Kennel" mode toggle is intentionally left alone
  // so admins can still preview the user-facing experience when needed.
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  const isBuyer = persona === "buyer";
  const isTransporter = persona === "transporter";
  const isIntlTransporter = persona === "international_transporter";
  const isProvider = persona === "service_provider";
  const isStorefront = persona === "storefront_seller";
  const isPetOwner = persona === "owner" || persona === "dog_owner";
  const headline = PERSONA_HEADLINE[persona] || PERSONA_HEADLINE.owner;
  const newDogLabel = PERSONA_NEW_DOG_LABEL[persona] || PERSONA_NEW_DOG_LABEL.owner;

  return (
    <AppShell>
      <PersonaPickerDialog />
      <WidgetBoundary name="admin-alert-strip"><AdminAlertStrip user={user} /></WidgetBoundary>
      <WidgetBoundary name="seller-welcome-banner"><SellerWelcomeBanner /></WidgetBoundary>
      {/* Onboarding nudge for breeders/owners without a kennel yet.
          Skip entirely for service-provider + storefront-seller personas
          who shouldn't be nagged to set up a kennel they don't want. */}
      {persona !== "service_provider" && persona !== "storefront_seller" && (
        <WidgetBoundary name="create-kennel-nudge"><CreateKennelNudge persona={persona} /></WidgetBoundary>
      )}
      <WidgetBoundary name="verified-seal-kit"><VerifiedSealKitAnnouncement /></WidgetBoundary>
      <WidgetBoundary name="provider-application-nudge"><ProviderApplicationNudge persona={persona} /></WidgetBoundary>
      <WidgetBoundary name="next-step-nudge"><NextStepNudge /></WidgetBoundary>
      {/* TransporterApplicationNudge is now subsumed by TransporterDashboardBody
          for transporter persona — keep the nudge only for any non-transporter
          users who somehow have a transporter row (edge case via direct API). */}
      {persona !== "transporter" && (
        <WidgetBoundary name="transporter-nudge"><TransporterApplicationNudge persona={persona} /></WidgetBoundary>
      )}
      <WidgetBoundary name="persona-greeting"><PersonaGreeting /></WidgetBoundary>
      {/* Multi-hat summary strip — one compact card per extra persona.
          Renders nothing for single-persona members. */}
      <WidgetBoundary name="multi-hat-cards"><MultiHatCards /></WidgetBoundary>
      {/* Unread provider↔client conversations — clients/breeders/buyers
          get the banner; providers see a Messages tile in their own
          dashboard body instead. */}
      {!isProvider && (
        <WidgetBoundary name="unread-threads-nudge"><UnreadThreadsNudge /></WidgetBoundary>
      )}
      <WidgetBoundary name="onboarding-checklist"><OnboardingChecklist /></WidgetBoundary>
      {/* One-time persona orientation map — where the calendar,
          availability, bookings, payouts etc. live for THIS persona. */}
      <WidgetBoundary name="persona-toolkit">
        <PersonaToolkitCard persona={persona === "owner" ? "dog_owner" : persona} />
      </WidgetBoundary>
      {/* Payment recovery banner — surfaces whenever ANY subscription
          is in past_due state (auto-dismisses on successful retry). */}
      <WidgetBoundary name="payment-recovery-banner"><PaymentRecoveryBanner /></WidgetBoundary>
      {/* Contracts awaiting signature — top priority because it blocks
          marketplace transactions. Auto-hides when nothing's pending. */}
      <WidgetBoundary name="contracts-awaiting-banner"><ContractsAwaitingBanner /></WidgetBoundary>
      {/* Stripe Connect payouts banner — only for personas that SELL
          (breeders, providers, storefront sellers) + legacy accounts
          with no declared persona. Buyers, transporters, and pet
          owners should never see "you can't get paid yet". */}
      {(persona === "breeder" || isProvider || isStorefront || !persona) && (
        <WidgetBoundary name="stripe-connect-banner"><StripeConnectBanner /></WidgetBoundary>
      )}
      {/* "Bring your dog home" tracker — surfaces for every released
          escrow so buyers stay engaged through the take-home journey. */}
      <WidgetBoundary name="home-tracker"><HomeTrackerWidget /></WidgetBoundary>
      {/* Breeding Hub hero card — only renders for breeders / owners
          (not buyers, transporters, providers, or storefront sellers). */}
      {!isBuyer && !isTransporter && !isIntlTransporter && !isProvider && !isStorefront && (
        <WidgetBoundary name="breeding-hub-card"><BreederBreedingHubCard persona={persona} /></WidgetBoundary>
      )}
      {/* Calendar widget — for transporters it carries their pickup /
          delivery schedule; for service providers their confirmed
          appointments; for breeders/owners heat cycles, litters, and
          birthdays. Self-hides when there are zero events. Storefronts
          keep it hidden (orders live in their dashboard body). */}
      {!isStorefront && (
        <WidgetBoundary name="calendar-widget"><CalendarWidget /></WidgetBoundary>
      )}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
            {isTransporter
              ? "Armstrong Transport"
              : isProvider
              ? "Armstrong Service Providers"
              : isStorefront
              ? "Armstrong Storefronts"
              : "Registry overview"}
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="dashboard-headline">{headline}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isTransporter ? (
            <>
              <Link to="/transport/apply">
                <Button className="bg-ink hover:bg-crimson text-white rounded-full px-6 h-11" data-testid="dashboard-apply-transporter-btn">
                  <Truck size={16} className="mr-1.5" weight="duotone" /> Application
                </Button>
              </Link>
              <Link to="/transport/getting-started">
                <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="dashboard-transporter-help-btn">
                  How it works
                </Button>
              </Link>
            </>
          ) : isProvider ? (
            <>
              <Link to="/my-bookings">
                <Button className="bg-ink hover:bg-crimson text-white rounded-full px-6 h-11" data-testid="dashboard-provider-bookings-btn">
                  <HeartStraight size={16} className="mr-1.5" weight="duotone" /> {newDogLabel}
                </Button>
              </Link>
              <Link to="/provider/me">
                <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="dashboard-provider-profile-btn">
                  Edit profile
                </Button>
              </Link>
            </>
          ) : isStorefront ? (
            <>
              <Link to="/me/store">
                <Button className="bg-ink hover:bg-crimson text-white rounded-full px-6 h-11" data-testid="dashboard-storefront-manage-btn">
                  <Storefront size={16} className="mr-1.5" weight="duotone" /> {newDogLabel}
                </Button>
              </Link>
              <Link to="/me/store?tab=products">
                <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="dashboard-storefront-products-btn">
                  + New product
                </Button>
              </Link>
            </>
          ) : isBuyer ? (
            <>
              <Link to="/breeders">
                <Button className="bg-ink hover:bg-crimson text-white rounded-full px-6 h-11" data-testid="browse-kennels-btn">
                  <MagnifyingGlass size={16} className="mr-1.5" weight="bold" /> Browse kennels
                </Button>
              </Link>
              <Link to="/litters">
                <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="browse-litters-btn">
                  <PawPrint size={16} className="mr-1.5" weight="duotone" /> Browse litters
                </Button>
              </Link>
            </>
          ) : (
            <>
              <Link to="/dog-log">
                <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11 bg-[#FBF7EC] hover:bg-[#F3E9C7] border-[#E8D9A6] text-ink" data-testid="dashboard-dog-log-btn">
                  <HeartStraight size={16} className="mr-1.5 text-gold-dark" weight="duotone" /> Dog Log
                </Button>
              </Link>
              <Link to="/dogs/new">
                <Button className="bg-ink hover:bg-crimson text-white rounded-full px-6 h-11" data-testid="new-dog-btn">
                  <Plus size={16} className="mr-1" /> {newDogLabel}
                </Button>
              </Link>
              <Link to="/litters/new">
                <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="new-litter-btn">
                  <Plus size={16} className="mr-1" /> New litter
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Stat strip: buyers get a tighter 3-metric strip focused on what
          they're shopping. Transporters, service providers, and
          storefront sellers get NO registry stat strip — their metrics
          come from their dedicated dashboard bodies below. */}
      {isTransporter || isProvider || isStorefront ? null : isBuyer ? (
        <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-10">
          <Stat icon={UsersFour} label="Kennels" value={stats?.breeders ?? "—"} testId="stat-breeders" to="/breeders" />
          <Stat icon={PawPrint} label="Litters" value={stats?.litters ?? "—"} testId="stat-litters" to="/marketplace?tab=litters" />
          <Stat icon={Dog} label="Registered dogs" value={stats?.dogs ?? "—"} testId="stat-dogs" to="/marketplace" />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-10">
          <Stat icon={Dog} label="Dogs" value={stats?.dogs ?? "—"} testId="stat-dogs" to="/dogs" />
          <Stat icon={GenderMale} label="Males" value={stats?.males ?? "—"} testId="stat-males" to="/dogs?sex=male" />
          <Stat icon={GenderFemale} label="Females" value={stats?.females ?? "—"} testId="stat-females" to="/dogs?sex=female" />
          <Stat icon={PawPrint} label="Litters" value={stats?.litters ?? "—"} testId="stat-litters" to="/litters" />
          <Stat icon={UsersFour} label="Breeders" value={stats?.breeders ?? "—"} testId="stat-breeders" to="/breeders" />
        </div>
      )}

      {/* Dog-care widgets — irrelevant for transporters, service
          providers, and storefront sellers. */}
      {!isTransporter && !isIntlTransporter && !isProvider && !isStorefront && <TodayWithDogsWidget />}
      {!isTransporter && !isIntlTransporter && !isProvider && !isStorefront && <DogLogStreaksWidget />}

      {isTransporter ? (
        <TransporterDashboardBody />
      ) : isIntlTransporter ? (
        <InternationalTransporterDashboardBody />
      ) : isProvider ? (
        <ProviderDashboardBody />
      ) : isStorefront ? (
        <StorefrontDashboardBody />
      ) : isBuyer ? (
        <BuyerBody stats={stats} />
      ) : (
        <>
          {/* Breeder plan upsell + breeding matchmaker are breeder tools —
              pet owners keep the feed but skip the kennel monetization. */}
          {!isPetOwner && <BreederPlanPromo />}
          <SellerPayoutWaitlist />
          <StandardFeed stats={stats} />
          {!isPetOwner && <SmartMatches />}
        </>
      )}
    </AppShell>
  );
}

function BuyerBody({ stats }) {
  const hasPass = !!stats?.has_buyer_pass;
  const hasWatching = (stats?.watching_breeders?.length || 0) + (stats?.watching_litters?.length || 0) > 0;
  return (
    <>
      {hasPass ? (
        hasWatching ? <WatchingSection stats={stats} /> : null
      ) : (
        <BuyerPassPromo />
      )}
      <SmartMatches />
      <BuyerFeed stats={stats} />
    </>
  );
}
